import type { Container, PointData } from 'pixi.js';
import { Bounds } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { ResourceMap } from 'app/resources/types';
import cloneDeep from 'lodash/cloneDeep';
import { playLayoutAnimation } from 'libs/layouts/layoutAnimations/playLayoutAnimation';
import type { InventoryItemAmountView } from 'views/InventoryItemAmountView';
import { createInventoryItemViews } from 'views/InventoryItemAmountView';
import MathUtils from 'utils/MathUtils';
import { triggerParticleFX } from 'libs/particles/triggerParticleFX';
import ParticlesConfig from '../../../../assets/particles{ignore}/Win_Streak_Reward_Particles.json';
import type { FXParticleEmitterConfig } from '../../../libs/particles/FXParticleEmitterConfig';
import { FXParticleLayer } from '../../../libs/particles/FXParticleLayer';
import { interpolationMap } from '../../../libs/TweenManager/interpolations';
import { InventoryItem } from '../../../app/resources/types';
import type { Popup } from '../../../popups/Popup';
import { BasicRewardChestPopupLayout } from '../../../../layouts/popups/BasicRewardChestPopupLayout';
import { BasicRewardChestLayout } from '../../../../layouts/popups/rewardChests/BasicRewardChestLayout';
import type { RewardChestLayout, RewardChestPopupLayout } from '../../../libs/layouts/types/LayoutShapes';

export interface RewardChestPopupConfig {
  rewards: ResourceMap;
  popupLayout?: RewardChestPopupLayout;
  chestLayout?: RewardChestLayout;
}

export class RewardChestPopup implements Popup {
  public readonly onCollectClicked = new Signal();

  private readonly layout: RewardChestPopupLayout;

  private readonly chest: RewardChestLayout;

  public readonly root: Container;

  public readonly initialBounds = new Bounds(0, 0, 780, 1280);

  private readonly rewardBubbles: InventoryItemAmountView[] = [];

  private readonly openAnimation = 'Open';

  constructor(private readonly config: RewardChestPopupConfig) {
    const { popupLayout, chestLayout } = this.config;

    this.layout = popupLayout ?? new BasicRewardChestPopupLayout();
    this.root = this.layout.root;

    this.layout.collectButtonContainer.on('pointertap', () => {
      this.toggleCollectButton(false);
      this.onCollectClicked.emit();
    });

    this.chest = chestLayout ?? new BasicRewardChestLayout();
    this.chest.root.props({
      parent: this.layout.chestSlot,
    });

    this.toggleCollectButton(false);

    void this.animateOpen();
  }

  public async animateOpen(): Promise<void> {
    void playLayoutAnimation(this.chest, this.openAnimation, false, async (event) => {
      if (event === 'spawnGifts') {
        this.spawnRewards(this.chest.giftSpawnPoint.getGlobalPosition());
      }
    });
  }

  // TODO replace this logic with generic reward bubble collection animation support when available
  public async animateCollect(): Promise<void> {
    const { rewardBubbles, root } = this;

    if (rewardBubbles.length === 0) return;

    const fxLayer = new FXParticleLayer(true).props({
      parent: root.pixi.stage,
    });

    await Promise.all(
      rewardBubbles.map(async (bubble, index) => {
        const isCoin = bubble.getItemType() === InventoryItem.Coin;

        const delay = index * 100;
        const duration = 900 - delay;

        const target: PointData = isCoin
          ? { x: -100, y: 100 }
          : { x: root.pixi.renderer.width * 0.5, y: root.pixi.renderer.height + 100 };

        const tweenProps = isCoin
          ? {
              x: [bubble.x, target.x],
              y: [target.y, target.y],
            }
          : {
              x: [bubble.x > target.x ? bubble.x - 300 : bubble.x + 300, target.x],
              y: [bubble.y, target.y],
            };

        await bubble
          .tweenTo(tweenProps, duration, 'cubicIn', false, delay)
          .interpolation(interpolationMap.bezier)
          .start()
          .asyncOnComplete();

        const fxConfig = cloneDeep(ParticlesConfig as FXParticleEmitterConfig);
        if (bubble.getItemType() === InventoryItem.Coin) {
          fxConfig.emitAngle = 0;
        }

        fxLayer.addParticleEmitter(target.x, target.y, fxConfig);

        bubble.destroy({ children: true });
      }),
    );
  }

  private spawnRewards(spawnPos: PointData): void {
    const { rewards } = this.config;

    const bubbles = createInventoryItemViews(rewards).map((view) =>
      view.props({
        parent: this.root.pixi.stage,
        position: spawnPos,
        scale: 0,
      }),
    );

    bubbles.forEach(async (bubble, index): Promise<void> => {
      const angle = bubbles.length > 1 ? -2.355 + (index / (bubbles.length - 1)) * 1.57 : -1.57;
      const { x, y } = MathUtils.getPointOnCircle(bubble.x, bubble.y, 200, angle);
      await bubble
        .tweenTo(
          {
            x,
            y,
            scaleXY: 1.5,
          },
          300,
          'appear1',
          true,
          index * 50,
        )
        .asyncOnComplete();
      await triggerParticleFX('Yellow_Glitter_From_Center', bubble);
      this.toggleCollectButton(true);
    });

    this.rewardBubbles.push(...bubbles);
  }

  private toggleCollectButton(value: boolean): void {
    this.layout.collectButtonContainer.visible = value;
    this.layout.collectButtonContainer.interactive = value;
  }
}
